import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { automationAPI } from '../lib/api';
import api from '../lib/api';
import { STATUS_DISPLAY_NAMES, PROJECT_STAGES } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Zap,
  Plus,
  Trash2,
  Edit,
  Loader2,
  Play,
  Clock,
  Bell,
  Mail,
  ArrowRight,
  History,
  Settings,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';

const ACTIVE_STAGES = PROJECT_STAGES.filter(s => s !== 'completed' && s !== 'archived');

const ACTION_TYPES = [
  { value: 'notify_team', label: 'Notify Team', icon: Bell, color: 'text-blue-600' },
  { value: 'send_email', label: 'Send Email to Customer', icon: Mail, color: 'text-emerald-600' },
  { value: 'send_email_and_notify', label: 'Send Email + Notify Team', icon: Zap, color: 'text-amber-600' },
  { value: 'move_stage', label: 'Move to Stage', icon: ArrowRight, color: 'text-purple-600' },
];

const DEFAULT_RULE = {
  name: '',
  trigger_stage: 'deposit_taken',
  days_in_stage: 2,
  action_type: 'send_email_and_notify',
  action_config: {},
  enabled: true,
};

export const PipelineAutomation = () => {
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_RULE);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [autoActions, setAutoActions] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, logsRes, actionsRes] = await Promise.all([
        automationAPI.getRules(),
        automationAPI.getLogs(30),
        api.get('/auto-actions').catch(() => ({ data: {} })),
      ]);
      setRules(rulesRes.data);
      setLogs(logsRes.data);
      setAutoActions(actionsRes.data || {});
    } catch (error) {
      toast.error('Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleAutoAction = async (key, currentEnabled) => {
    try {
      await api.post('/auto-actions', { [key]: !currentEnabled });
      setAutoActions(prev => ({ ...prev, [key]: { ...prev[key], enabled: !currentEnabled } }));
      toast.success(`${autoActions[key]?.label || key} ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleCreate = () => {
    setEditingRule(null);
    setFormData({ ...DEFAULT_RULE });
    setDialogOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      trigger_stage: rule.trigger_stage,
      days_in_stage: rule.days_in_stage,
      action_type: rule.action_type,
      action_config: rule.action_config || {},
      enabled: rule.enabled,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingRule) {
        await automationAPI.updateRule(editingRule.id, formData);
        toast.success('Rule updated');
      } else {
        await automationAPI.createRule(formData);
        toast.success('Rule created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await automationAPI.deleteRule(ruleId);
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggle = async (rule) => {
    try {
      await automationAPI.updateRule(rule.id, { enabled: !rule.enabled });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r));
      toast.success(rule.enabled ? 'Rule disabled' : 'Rule enabled');
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await automationAPI.runNow();
      const total = res.data.results?.reduce((sum, r) => sum + (r.customers_triggered || 0), 0) || 0;
      toast.success(`Automation executed: ${total} actions triggered`);
      fetchData();
    } catch (error) {
      toast.error('Failed to run automation');
    } finally {
      setRunning(false);
    }
  };

  const getActionInfo = (type) => ACTION_TYPES.find(a => a.value === type) || ACTION_TYPES[0];

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="pipeline-automation-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Zap className="w-7 h-7 text-amber-500" />
              Pipeline Automation
            </h1>
            <p className="text-slate-500 mt-1">
              Automate actions when customers stay in a stage too long
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowLogs(!showLogs)}
              data-testid="toggle-logs-btn"
            >
              <History className="w-4 h-4 mr-2" />
              {showLogs ? 'Hide Logs' : 'View Logs'}
            </Button>
            <Button
              variant="outline"
              onClick={handleRunNow}
              disabled={running}
              data-testid="run-now-btn"
            >
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Run Now
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleCreate}
              data-testid="create-rule-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Rule
            </Button>
          </div>
        </div>

        {/* Auto-Actions Toggles */}
        <Card className="border-slate-200" data-testid="auto-actions-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="w-4 h-4 text-slate-500" />
              Automated Tasks
            </CardTitle>
            <CardDescription>Enable or disable automatic actions triggered by status changes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(autoActions).map(([key, action]) => (
              <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{action.label}</p>
                  <p className="text-xs text-slate-500">{action.description} <Badge variant="outline" className="ml-1 text-[10px]">{STATUS_DISPLAY_NAMES[action.trigger] || action.trigger}</Badge></p>
                </div>
                <Switch
                  checked={action.enabled}
                  onCheckedChange={() => handleToggleAutoAction(key, action.enabled)}
                  data-testid={`toggle-${key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Rules List */}
        <div className="grid gap-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : rules.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Zap className="w-12 h-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">No automation rules yet</p>
                <p className="text-sm mt-1">Create your first rule to automate pipeline actions</p>
                <Button className="mt-4 bg-amber-500 hover:bg-amber-600 text-white" onClick={handleCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Rule
                </Button>
              </CardContent>
            </Card>
          ) : (
            rules.map((rule) => {
              const actionInfo = getActionInfo(rule.action_type);
              const ActionIcon = actionInfo.icon;
              return (
                <Card key={rule.id} className={`border-slate-200 transition-opacity ${!rule.enabled ? 'opacity-60' : ''}`} data-testid={`rule-card-${rule.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-slate-900">{rule.name}</h3>
                          <Badge variant={rule.enabled ? 'default' : 'secondary'} className={rule.enabled ? 'bg-emerald-100 text-emerald-700' : ''}>
                            {rule.enabled ? 'Active' : 'Disabled'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                          <span>When customer is in</span>
                          <Badge variant="outline" className="font-medium">
                            {STATUS_DISPLAY_NAMES[rule.trigger_stage] || rule.trigger_stage}
                          </Badge>
                          <span>for</span>
                          <Badge variant="outline" className="font-medium">
                            <Clock className="w-3 h-3 mr-1" />
                            {rule.days_in_stage} days
                          </Badge>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                          <Badge variant="outline" className={`font-medium ${actionInfo.color}`}>
                            <ActionIcon className="w-3 h-3 mr-1" />
                            {actionInfo.label}
                          </Badge>
                        </div>
                        {rule.last_run && (
                          <p className="text-xs text-slate-400 mt-2">
                            Last run: {formatDateTime(rule.last_run)} ({rule.run_count || 0} total actions)
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={rule.enabled}
                          onCheckedChange={() => handleToggle(rule)}
                          data-testid={`toggle-rule-${rule.id}`}
                        />
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(rule)} data-testid={`edit-rule-${rule.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(rule.id)} data-testid={`delete-rule-${rule.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Automation Logs */}
        {showLogs && (
          <Card className="border-slate-200" data-testid="automation-logs">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                Execution Log
              </CardTitle>
              <CardDescription>Recent automation actions</CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No automation logs yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rule</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Executed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">{log.rule_name}</TableCell>
                        <TableCell>{log.customer_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {STATUS_DISPLAY_NAMES[log.trigger_stage] || log.trigger_stage}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs capitalize">
                          {log.action_type?.replace(/_/g, ' ')}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {formatDateTime(log.executed_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Rule Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Automation Rule'}</DialogTitle>
              <DialogDescription>
                Define a trigger condition and action for the pipeline
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Rule Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Follow up on Deposit Taken"
                  data-testid="rule-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Trigger Stage</Label>
                  <Select
                    value={formData.trigger_stage}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, trigger_stage: v }))}
                  >
                    <SelectTrigger data-testid="trigger-stage-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVE_STAGES.map(stage => (
                        <SelectItem key={stage} value={stage}>
                          {STATUS_DISPLAY_NAMES[stage]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Days in Stage</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={formData.days_in_stage}
                    onChange={(e) => setFormData(prev => ({ ...prev, days_in_stage: parseInt(e.target.value) || 1 }))}
                    data-testid="days-in-stage-input"
                  />
                </div>
              </div>

              <div>
                <Label>Action</Label>
                <Select
                  value={formData.action_type}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, action_type: v }))}
                >
                  <SelectTrigger data-testid="action-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_TYPES.map(action => (
                      <SelectItem key={action.value} value={action.value}>
                        {action.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(formData.action_type === 'send_email' || formData.action_type === 'send_email_and_notify') && (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                  <Label className="text-xs text-slate-500 uppercase">Email Configuration</Label>
                  <div>
                    <Label className="text-sm">Subject</Label>
                    <Input
                      value={formData.action_config?.email_subject || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        action_config: { ...prev.action_config, email_subject: e.target.value }
                      }))}
                      placeholder="Follow up email subject..."
                      data-testid="email-subject-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Body (HTML)</Label>
                    <Textarea
                      value={formData.action_config?.email_body || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        action_config: { ...prev.action_config, email_body: e.target.value }
                      }))}
                      placeholder="<p>Hi {{first_name}}, ...</p>"
                      rows={4}
                      data-testid="email-body-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Variables: {'{{first_name}}'}, {'{{last_name}}'}, {'{{customer_name}}'}
                    </p>
                  </div>
                </div>
              )}

              {formData.action_type === 'move_stage' && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="text-xs text-slate-500 uppercase">Target Stage</Label>
                  <Select
                    value={formData.action_config?.target_stage || ''}
                    onValueChange={(v) => setFormData(prev => ({
                      ...prev,
                      action_config: { ...prev.action_config, target_stage: v }
                    }))}
                  >
                    <SelectTrigger data-testid="target-stage-select" className="mt-2">
                      <SelectValue placeholder="Select target stage" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVE_STAGES.map(stage => (
                        <SelectItem key={stage} value={stage}>
                          {STATUS_DISPLAY_NAMES[stage]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.enabled}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, enabled: v }))}
                  data-testid="rule-enabled-switch"
                />
                <Label>Rule enabled</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-rule-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
